import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Target, ThumbsUp } from 'lucide-react';
import { SatisfactionMetrics as SatisfactionData } from '@/hooks/useDashboardMetrics';

interface ResolutionRateProps {
  data: SatisfactionData | undefined;
  isLoading: boolean;
}

const ResolutionRate = ({ data, isLoading }: ResolutionRateProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        </CardHeader>
        <CardContent>
          <div className="text-center animate-pulse">
            <div className="h-12 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 bg-gray-200 rounded mb-2"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const getResolutionColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-600">
          <Target className="h-5 w-5" />
          Taxa de Resolução
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center">
          {/* Percentage */}
          <div className={`text-4xl font-bold mb-2 ${getResolutionColor(data.firstContactResolution)}`}>
            {data.firstContactResolution}%
          </div>
          <p className="text-sm text-gray-600 mb-4">Primeiro Contato</p>

          {/* Progress Circle Visual */}
          <div className="relative w-24 h-24 mx-auto mb-4">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="#e5e7eb"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke={data.firstContactResolution >= 90 ? '#22c55e' : data.firstContactResolution >= 75 ? '#eab308' : '#ef4444'}
                strokeWidth="8"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 40}`}
                strokeDashoffset={`${2 * Math.PI * 40 * (1 - data.firstContactResolution / 100)}`}
                className="transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <ThumbsUp className={`h-6 w-6 ${getResolutionColor(data.firstContactResolution)}`} />
            </div>
          </div>

          {/* Status e Meta */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span>Meta: 90%</span>
              <span className={data.firstContactResolution >= 90 ? 'text-green-600' : 'text-red-600'}>
                {data.firstContactResolution >= 90 ? '✓ Meta atingida' : '⚠ Abaixo da meta'}
              </span>
            </div>
            <Progress value={data.firstContactResolution} className="h-2" />
          </div>

          {/* Informações Adicionais */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600">
              <div className="flex justify-between mb-1">
                <span>Resolvidos no 1º contato:</span>
                <span className="font-semibold">
                  {Math.round((data.firstContactResolution / 100) * 150)} de 150
                </span>
              </div>
              <div className="flex justify-between">
                <span>Necessitaram follow-up:</span>
                <span className="font-semibold">
                  {150 - Math.round((data.firstContactResolution / 100) * 150)} tickets
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResolutionRate;
